import React, {useEffect, useState} from 'react';
import styles from "./RentalCosts.module.scss";

import {Box, LoadingDecorator} from '@renta-apps/renta-react-components';
import RentaLineChart, {ChartData} from '@/components/Charts/LineChart/RentaLineChart';
import {calculateMovingAverage} from '@/helpers/ChartDataHelper';
import {formatDate} from "@/helpers/DateHelper";
import RentaEasyConstants from '@/helpers/RentaEasyConstants';
import Localizer from '@/localization/Localizer';
import BasicChartModel from "@/models/server/BasicChartModel";
import {getConstructionSitesRentalCosts} from '@/services/ConstructionSiteService';

interface RentalCostsProps {
    constructionSiteId: string;
}

const RentalCosts: React.FC<RentalCostsProps> = ({constructionSiteId}) => {
    const [data, setData] = useState<ChartData[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const chartData: BasicChartModel[] = await getConstructionSitesRentalCosts(constructionSiteId);
                const dataWithMovingAverage = calculateMovingAverage(chartData, 7);

                setData(dataWithMovingAverage);
            } catch (error) {
                console.error('Error fetching rental costs data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [constructionSiteId]);

    return (
        <LoadingDecorator className={styles.loadingContainer} hideConentOnLoading={true} isLoading={loading}>
            {
                (data.length > 0) && (
                    <RentaLineChart data={data}
                                    xKey="date"
                                    lines={[
                                        {
                                            dataKey: "value",
                                            dataName: Localizer.get(Localizer.constructionSiteDetailsStatusRentalCostsChartLegend, RentaEasyConstants.environmentCurrencySymbol),
                                            stroke: RentaEasyConstants.Colors.brightOrange,
                                            strokeWidth: 3,
                                            gradientId: "lineGradient",
                                            type: "linear",
                                        },
                                        {
                                            dataKey: "movingAverage",
                                            dataName: Localizer.genericChartMovingAverage,
                                            stroke: RentaEasyConstants.Colors.lightPurple,
                                            strokeWidth: 3,
                                            gradientId: "movingAverage",
                                            type: "monotone",
                                        },
                                    ]}
                                    title={Localizer.get(Localizer.constructionSiteDetailsStatusRentalCostsChartCurrentCost, data[data.length - 1]?.value.toFixed(2), RentaEasyConstants.environmentCurrencySymbol)}
                                    subtitle={`${formatDate(data[data.length - 1]?.date)}`}
                                    legendItems={[
                                        {label: Localizer.get(Localizer.constructionSiteDetailsStatusRentalCostsChartLegend, RentaEasyConstants.environmentCurrencySymbol), color: "#FE5000"},
                                        {label: Localizer.genericChartMovingAverage, color: "#116CEA"},
                                    ]}
                                    xAxisFormatter={(date) => {
                                        const parsedDate = new Date(date);
                                        return `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}.${parsedDate.getFullYear().toString().slice(-2)}`;
                                    }}
                                    brush={true}
                    />
                )
            }
            {
                (data.length === 0) && (
                    <Box className={styles.emptyConsumptionContainer} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                        <span data-cy={"status-rental-costs-chart-no-data"}>{Localizer.genericNoData}</span>
                    </Box>
                )
            }
        </LoadingDecorator>
    );
};

export default RentalCosts;